import { useState } from "react";
import { Check } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { orderStatusLabel } from "@gaole/shared";
import { Badge, Button, Input, Spinner, statusColor } from "../components/ui";
import { PageHeader } from "../components/storefront/PageHeader";

interface TrackResult {
  orderNo: string;
  status: string;
  trackingNo: string | null;
  courier: string | null;
  items: { name: string; quantity: number }[];
  steps: { key: string; label: string; at: string | null }[];
  statusLabel?: string;
  easyTrackUrl: string | null;
}

export function Track() {
  const { orderNo: paramOrderNo } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState(paramOrderNo ?? "");

  const { data, isLoading, error } = useQuery({
    queryKey: ["track", paramOrderNo],
    queryFn: () => api<TrackResult>(`/track/${encodeURIComponent(paramOrderNo!)}`),
    enabled: Boolean(paramOrderNo),
    retry: false,
  });

  return (
    <div className="space-y-5 px-4 pt-4">
      <PageHeader
        title="Track order"
        description="Enter your order number to see live status and delivery steps."
      />

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (input.trim()) navigate(`/track/${encodeURIComponent(input.trim().toUpperCase())}`);
        }}
      >
        <Input
          placeholder="Order number"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="font-mono uppercase"
        />
        <Button type="submit">Track</Button>
      </form>

      {isLoading && (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      )}

      {error != null && (
        <p className="rounded-2xl bg-red-500/10 px-4 py-3 text-center text-xs text-red-300">
          {error instanceof ApiError ? error.message : "Order not found — double-check the number."}
        </p>
      )}

      {data && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-white/5 bg-surface p-4">
            <div className="flex items-center justify-between">
              <span className="font-mono text-sm font-bold">{data.orderNo}</span>
              <Badge color={statusColor(data.status)}>{data.statusLabel ?? orderStatusLabel(data.status)}</Badge>
            </div>
            <p className="mt-1.5 text-[11px] text-white/50">
              {data.items.map((i) => `${i.name} ×${i.quantity}`).join(" · ")}
            </p>
            {data.trackingNo && (
              <div className="mt-3 rounded-xl bg-white/5 p-3 text-center">
                <p className="text-[10px] text-white/50">{data.courier ?? "Courier"} AWB</p>
                {data.easyTrackUrl ? (
                  <a
                    href={data.easyTrackUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono text-sm font-semibold text-brand-pink underline"
                  >
                    {data.trackingNo}
                  </a>
                ) : (
                  <p className="font-mono text-sm font-semibold text-brand-pink">{data.trackingNo}</p>
                )}
                <p className="mt-1 text-[9px] text-white/35">Tap to view live courier scans on EasyTrack</p>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="rounded-2xl border border-white/5 bg-surface p-4">
            {data.steps.map((step, i) => (
              <div key={step.key} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div
                    className={`flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold ${
                      step.at ? "bg-gradient-brand text-white" : "border border-white/15 text-white/30"
                    }`}
                  >
                    {step.at ? <Check size={10} strokeWidth={3} /> : ""}
                  </div>
                  {i < data.steps.length - 1 && (
                    <div className={`w-0.5 flex-1 ${step.at ? "bg-brand-pink/60" : "bg-white/10"}`} style={{ minHeight: 24 }} />
                  )}
                </div>
                <div className="pb-5">
                  <p className={`text-xs font-medium ${step.at ? "text-white" : "text-white/35"}`}>{step.label}</p>
                  {step.at && <p className="text-[10px] text-white/40">{fmtDate(step.at)}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
